import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Checkbox,
  ClickAwayListener,
  FormControl,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useStyles from "./data-table-filter-form.style";
import { OcErrorBoundary } from "oc/components";

interface TableData {
  filterField: string;
  filterSelected: string[];
  filterValues: string[];
  filterShowForm: boolean;
}

interface OcDataTableFilterFormProps {
  tableData: TableData;
  setTableData: (data: Partial<TableData>) => void;
}

export const OcDataTableFilterForm: React.FC<OcDataTableFilterFormProps> = ({
  tableData,
  setTableData,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const { filterField, filterSelected, filterValues, filterShowForm } =
    tableData;

  useEffect(() => {
    const handleEscKeypress = (e: KeyboardEvent) => {
      if (e.key === "Escape") setTableData({ filterShowForm: false });
    };
    window.addEventListener("keydown", handleEscKeypress);
    return () => window.removeEventListener("keydown", handleEscKeypress);
  }, [filterShowForm, setTableData]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    const name = event.target.name;

    if (name === "all") {
      return setTableData({ filterSelected: [] });
    }

    if (!checked) {
      setTableData({
        filterSelected: filterSelected.filter((item) => item !== name),
      });
    } else {
      setTableData({
        filterSelected: [...filterSelected, name],
      });
    }
  };

  return (
    <OcErrorBoundary>
      <ClickAwayListener
        onClickAway={() => setTableData({ filterShowForm: false })}
      >
        <Box
          id="table-filter-search-form"
          className={classes.root}
          style={{ right: 0 }}
        >
          <FormControl component="fieldset" sx={{ p: "10px 17px" }}>
            <FormGroup>
              {/* “(Minden)” */}
              <FormControlLabel
                control={
                  <Checkbox
                    id={filterField}
                    name="all"
                    checked={
                      filterSelected.length === filterValues.length ||
                      filterSelected.length === 0
                    }
                    onChange={handleChange}
                    sx={{ color: theme.palette.gray80.main }}
                  />
                }
                label="(Minden)"
                labelPlacement="end"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    color: theme.palette.gray80.main,
                  },
                }}
              />

              <Box className={classes.line} />

              {filterValues.map((item, idx) => (
                <FormControlLabel
                  key={idx}
                  control={
                    <Checkbox
                      id={`filter-value-${idx}`}
                      name={item}
                      checked={filterSelected.includes(item)}
                      onChange={handleChange}
                      sx={{ color: theme.palette.gray80.main }}
                    />
                  }
                  label={
                    ["state", "type", "typeName", "leaveBadges"].includes(filterField)
                      ? t(`MY_LEAVE_${item.toUpperCase()}`)
                      : item
                  }
                  labelPlacement="end"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      color: theme.palette.gray80.main,
                    },
                  }}
                />
              ))}
            </FormGroup>
          </FormControl>
        </Box>
      </ClickAwayListener>
    </OcErrorBoundary>
  );
};

export default OcDataTableFilterForm;
